import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type PortalType = 'admin' | 'institution' | 'student' | 'partner' | 'early_pathways' | null;

interface PortalCard {
  type: PortalType;
  label: string;
  subtitle: string;
  icon: string;
  accentColor: string;
}

const PORTALS: PortalCard[] = [
  {
    type: 'student',
    label: 'Athlete Portal',
    subtitle: 'Access your BTHD development program',
    icon: '',
    accentColor: '#0fa86d',
  },
  {
    type: 'early_pathways',
    label: 'Early Pathways',
    subtitle: 'Youth sport development program',
    icon: '',
    accentColor: '#bf1e2e',
  },
  {
    type: 'admin',
    label: 'BTHD Admin',
    subtitle: 'Platform administration and analytics',
    icon: '',
    accentColor: '#bf1e2e',
  },
  {
    type: 'institution',
    label: 'Institution / Organization',
    subtitle: 'School, sports org, or player association',
    icon: '',
    accentColor: '#014c8d',
  },
  {
    type: 'partner',
    label: 'Partner Access',
    subtitle: 'Corporations, wealth management, and investor portal',
    icon: '',
    accentColor: '#014c8d',
  },
];

const LOGIN_LABELS: Record<NonNullable<PortalType>, string> = {
  admin: 'BTHD Admin Login',
  institution: 'Institution Login',
  student: 'Athlete Portal Login',
  partner: 'Partner Access',
  early_pathways: 'Early Pathways',
};

export default function LoginPage() {
  const [portalType, setPortalType] = useState<PortalType>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const selectedPortal = PORTALS.find(p => p.type === portalType) ?? null;

  const handleBack = () => {
    setPortalType(null);
    setEmail('');
    setPassword('');
    setError('');
    setLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });

      if (authError || !authData.user) {
        setError('Invalid email or password.');
        setLoading(false);
        return;
      }

      // Athlete Portal: no is_admin check — just route to /athlete
      if (portalType === 'student') {
        navigate('/athlete');
        return;
      }

      // Admin + Institution: require is_admin flag
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin, institution_type')
        .eq('id', authData.user.id)
        .single();

      if (!profile?.is_admin) {
        await supabase.auth.signOut();
        setError('Admin access required. Please use the BTHD mobile app to sign in.');
        setLoading(false);
        return;
      }

      navigate('/admin');
    } catch {
      setError('Sign in failed. Try again.');
      setLoading(false);
    }
  };

  const accent = selectedPortal?.accentColor ?? '#bf1e2e';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Barlow, sans-serif',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: portalType ? 400 : 480 }}>

        {/* Logo + wordmark */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <img
            src="/bthd-email-sig-300x80-transparent.png"
            alt="Bigger Than Hoop Dreams"
            style={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)', marginBottom: 16 }}
          />
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
            {portalType ? LOGIN_LABELS[portalType] : 'Sign in to your portal'}
          </div>
        </div>

        {/* ── 3-card selector ── */}
        {!portalType && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PORTALS.map(portal => (
              <button
                key={portal.type}
                onClick={() => {
                  if (portal.type === 'partner') { window.location.href = '/partner.html'; return; }
                  if (portal.type === 'early_pathways') { window.location.href = '/early-pathways/login'; return; }
                  setPortalType(portal.type);
                }}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.07)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = portal.accentColor;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.1)';
                }}
              >
                {/* Left accent bar */}
                <div style={{
                  width: 3, alignSelf: 'stretch', borderRadius: 2, flexShrink: 0,
                  background: portal.accentColor,
                }} />
                {/* Text */}
                <div style={{ flex: 1, padding: '2px 0' }}>
                  <div style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontWeight: 800, fontSize: 17, color: '#fff', letterSpacing: 0.3,
                  }}>
                    {portal.label}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
                    {portal.subtitle}
                  </div>
                </div>
                {/* Chevron */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            ))}
          </div>
        )}

        {/* ── Login form ── */}
        {portalType && (
          <>
            {/* Accent bar */}
            <div style={{
              height: 3, borderRadius: 2,
              background: accent,
              marginBottom: 28,
            }} />

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 14 }}>
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '14px 16px', borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '14px 16px', borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>

              {error && (
                <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 14, textAlign: 'center' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10,
                  background: accent, border: 'none', color: '#fff',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: 700, fontSize: 16, letterSpacing: 1, cursor: 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Signing in…' : 'SIGN IN'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button
                onClick={handleBack}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, color: 'rgba(255,255,255,0.4)',
                  fontFamily: 'Barlow, sans-serif',
                }}
              >
                ← Back
              </button>
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <a href="/" style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textDecoration: 'none' }}>
            ← Back to bthd.app
          </a>
        </div>
      </div>
    </div>
  );
}
